// src/components/Admin/RutasDidacticasComp/RutasDidacticasForm/RutasDidacticasFormSteps.jsx

import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Button,
  TextArea,
  Confirm,
  Dropdown,
  Loader,
  Image,
  Checkbox,
  Icon,
} from "semantic-ui-react";
import { useFormik } from "formik";
import {
  buildInitialValues,
  validationSchema,
} from "./RutasDidacticasForm.form";
import { DidacticRoutesAPI, AssistanceApi } from "../../../../api";

const didacticRoutesAPI = new DidacticRoutesAPI();
const assistanceApi = new AssistanceApi();

/**
 * RutasDidacticasFormSteps:
 * - Paso 1: Crear ruta
 * - Paso 2: Seleccionar asistentes y agregarlos a la ruta
 */
export function RutasDidacticasFormSteps({
  accessToken,
  teacher,
  onClose,
  onReload,
  forceCreateMode,
}) {
  // 1. Estado del paso actual: 1 (crear ruta), 2 (seleccionar asistentes)
  const [step, setStep] = useState(1);

  // 2. Estado para control de carga y confirm
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  // 3. Estado donde guardamos la ruta creada (para usar su _id en Paso 2)
  const [createdRoute, setCreatedRoute] = useState(null);

  // 4. Formik para crear la ruta en Paso 1
  const formik = useFormik({
    initialValues: buildInitialValues(),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: () => setShowConfirm(true), // Abrir confirm antes de crear
  });

  // 5. Generar opciones de "classNumber" (dropdown)
  const classOptions = (teacher?.classrole || []).map((item) => ({
    key: item.classnumber,
    text: item.classnumber,
    value: item.classnumber,
  }));

  // 6. Lógica para Confirmar la creación de la ruta
  const handleConfirm = async () => {
    setShowConfirm(false);
    setLoading(true);

    try {
      // a) Construir data para crear ruta
      const data = {
        nameRute: formik.values.nameRute,
        description: formik.values.description,
        classnumber: formik.values.classnumber,
        institution_Id: teacher.institution,
      };

      // b) Llamar a createDidacticRoute
      const result = await didacticRoutesAPI.createDidacticRoute(
        accessToken,
        teacher._id,
        teacher.institution._id,
        data
      );
      console.log("Nueva ruta creada =>", result.route);

      // c) Guardar la ruta en estado local para el Paso 2
      setCreatedRoute(result.route);

      // d) Pasar al Paso 2
      setStep(2);
    } catch (error) {
      console.error("Error al crear la ruta:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = () => setShowConfirm(false);

  // ======================
  // PASO 2: Seleccionar Asistentes
  // ======================
  const [assistants, setAssistants] = useState([]);
  const [selectedAssistants, setSelectedAssistants] = useState([]);

  // Cargar asistentes cuando entremos al Paso 2
  useEffect(() => {
    if (step === 2) {
      fetchAssistants();
    }
  }, [step]);

  const fetchAssistants = async () => {
    try {
      setLoading(true);
      const response = await assistanceApi.getAssistances(accessToken, 1, 100);
      // Devuelve { assistances: { docs: [...], ... } }
      const docs = response?.assistances?.docs || [];
      setAssistants(docs);
    } catch (error) {
      console.error("Error al obtener asistentes:", error);
    } finally {
      setLoading(false);
    }
  };

  // Manejar el toggle/checkbox de cada asistente
  const handleToggleAssistant = (assistantId) => {
    setSelectedAssistants((prev) => {
      if (prev.includes(assistantId)) {
        // Si ya está, lo quitamos
        return prev.filter((id) => id !== assistantId);
      } else {
        // Si no está, lo agregamos
        return [...prev, assistantId];
      }
    });
  };

  // Confirmar la selección de asistentes (insertAssistantsInRoute)
  const handleSaveAssistants = async () => {
    if (!createdRoute?._id) {
      console.warn("No se tiene el ID de la ruta creada");
      return;
    }
    setLoading(true);
    try {
      await didacticRoutesAPI.insertAssistantsInRoute(
        accessToken,
        createdRoute._id,
        selectedAssistants
      );
      console.log("Asistentes agregados correctamente");

      // Podríamos cerrar el modal aquí o mostrar un mensaje
      // Actualizamos la lista de rutas en el parent
      onReload();
      // Cerrar modal
      onClose();
    } catch (error) {
      console.error("Error al insertar asistentes en la ruta:", error);
    } finally {
      setLoading(false);
    }
  };

  // Render de Paso 1
  const renderStep1 = () => (
    <>
      <Modal.Header>Crear Nuevo Itinerario Didáctico</Modal.Header>
      <Modal.Content>
        <Form onSubmit={formik.handleSubmit}>
          <Form.Input
            label="Nombre del Itinerario"
            name="nameRute"
            value={formik.values.nameRute}
            onChange={formik.handleChange}
            error={
              formik.errors.nameRute
                ? { content: formik.errors.nameRute, pointing: "above" }
                : null
            }
          />

          <Form.Field
            control={TextArea}
            label="Descripción del Itinerario"
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            error={
              formik.errors.description
                ? { content: formik.errors.description, pointing: "above" }
                : null
            }
          />

          {/* Dropdown de Class Number */}
          <Form.Field error={!!formik.errors.classnumber}>
            <label>Nombre del módulo o clase</label>
            <Dropdown
              placeholder="Selecciona un módulo o clase"
              fluid
              selection
              options={classOptions}
              value={formik.values.classnumber}
              onChange={(_, { value }) =>
                formik.setFieldValue("classnumber", value)
              }
            />
            {formik.errors.classnumber && (
              <div style={{ color: "red", marginTop: "0.5rem" }}>
                {formik.errors.classnumber}
              </div>
            )}
          </Form.Field>

          <Button type="submit" primary loading={loading}>
            Crear Itinerario
          </Button>
        </Form>
      </Modal.Content>

      {/* Confirm para crear ruta */}
      <Confirm
        open={showConfirm}
        onCancel={handleCancelConfirm}
        onConfirm={handleConfirm}
        content={`¿Estás seguro de que quieres crear una nueva ruta didáctica con el nombre "${formik.values.nameRute}"?`}
        cancelButton="No"
        confirmButton="Sí, continuar"
      />
    </>
  );

  // Render de Paso 2
  const renderStep2 = () => (
    <>
      <Modal.Header>Seleccionar Asistentes para el Itinerario</Modal.Header>
      <Modal.Content scrolling>
        {loading && <Loader active inline="centered" />}

        {!loading && (
          <>
            <p>
              Itinerario creado: <strong>{createdRoute?.nameRute}</strong>
            </p>
            <p>
              Marca los asistentes que quieras asignar a este itinerario y haz clic en
              <strong> Guardar</strong>.
            </p>

            <div style={{ maxHeight: 300, overflowY: "auto" }}>
              {assistants.map((assistant) => {
                const isChecked = selectedAssistants.includes(assistant._id);
                return (
                  <div
                    key={assistant._id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "1rem",
                    }}
                  >
                    <Image
                      src={assistant.miniature}
                      size="tiny"
                      circular
                      style={{ marginRight: "1rem" }}
                    />
                    <div style={{ flex: 1 }}>
                      <strong>{assistant.title}</strong>
                      <div style={{ fontSize: "0.85rem", color: "#666" }}>
                        {assistant.path}
                      </div>
                    </div>
                    <Checkbox
                      toggle
                      checked={isChecked}
                      onChange={() => handleToggleAssistant(assistant._id)}
                    />
                  </div>
                );
              })}
            </div>

            <Button
              primary
              onClick={handleSaveAssistants}
              loading={loading}
              disabled={selectedAssistants.length === 0}
            >
              <Icon name="save" />
              Guardar Asistentes
            </Button>
          </>
        )}
      </Modal.Content>
    </>
  );

  return (
    <Modal open={true} onClose={onClose} size="small">
      {step === 1 ? renderStep1() : renderStep2()}
    </Modal>
  );
}
